import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import "./TimeSelect.css";

/* 시간 고르개 = claude.ai 설정 창의 드롭다운 실측
   (docs/rail-reference.json settingsDialog.field · selectMenu, 2026-10-19 지윤 지시).
   네이티브 <input type="time"> 은 브라우저마다 생김새가 달라 저쪽과 맞출 수가 없다 —
   30분 단위 목록을 직접 그린다. 맨 위에 흐린 안내 줄, 고른 줄에는 파란 체크. */
const STEP = 30;
const SLOTS = Array.from({ length: (24 * 60) / STEP }, (_, index) => {
  const minutes = index * STEP;
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
});
/* 저쪽은 오전/오후 12시간제로 적는다. 우리 화면도 같은 꼴로 읽히게 한다. */
export const timeLabel = (value: string) => {
  const [h, m] = value.split(":").map(Number);
  const half = h < 12 ? "오전" : "오후";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${half} ${hour}:${String(m).padStart(2, "0")}`;
};

export function TimeSelect({ value, onChange, ariaLabel, placeholder = "시간", disabled }: {
  value: string; onChange: (next: string) => void;
  ariaLabel: string; placeholder?: string; disabled?: boolean;
}) {
  /* 2026-10-19 밤: claude.ai Quiet hours 드롭다운 실측(rail-reference.json `quietHours.menu`) 그대로 —
     맨 위는 흐린 글자 줄이 아니라 **검색 입력칸**(자리표시 = 시작/종료)이고, 열자마자 거기 커서가 선다.
     치면 목록이 걸러지고 엔터는 첫 줄을 고른다. 고른 줄은 바탕 5% + 파란 체크 20. */
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const word = query.replace(/\s/g, "");
  const shown = word ? SLOTS.filter(slot => timeLabel(slot).replace(/\s/g, "").includes(word) || slot.includes(word)) : SLOTS;
  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const away = (event: MouseEvent) => { if (!wrap.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); setOpen(false); } };
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", escape, true);
    search.current?.focus();
    /* 고른 시각이 보이게 목록을 그 자리로 내린다 — 자정부터 마흔여덟 칸이라 안 그러면 늘 맨 위다. */
    const picked = list.current?.querySelector<HTMLElement>("[aria-selected='true']");
    if (picked && list.current) list.current.scrollTop = picked.offsetTop - list.current.clientHeight / 2 + picked.clientHeight / 2;
    return () => { window.removeEventListener("mousedown", away); window.removeEventListener("keydown", escape, true); };
  }, [open]);
  const pick = (slot: string) => { onChange(slot); setOpen(false); };

  return <div className="time-select" ref={wrap}>
    <button type="button" className="time-select-trigger" aria-haspopup="listbox" aria-expanded={open}
      aria-label={ariaLabel} disabled={disabled} onClick={() => setOpen(current => !current)}>
      <span>{value ? timeLabel(value) : placeholder}</span>
      <ChevronDown size={16} aria-hidden="true" />
    </button>
    {open && <div className="time-select-menu">
      <input ref={search} className="time-select-search" role="combobox" aria-expanded="true" aria-controls={`${ariaLabel}-list`}
        aria-label={`${ariaLabel} 찾기`} placeholder={placeholder} value={query}
        onChange={event => setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === "Enter" && shown[0]) { event.preventDefault(); pick(shown[0]); } }} />
      <div className="time-select-list" role="listbox" id={`${ariaLabel}-list`} aria-label={ariaLabel} ref={list}>
        {shown.map(slot => <button key={slot} type="button" role="option" aria-selected={slot === value} onClick={() => pick(slot)}>
          <span>{timeLabel(slot)}</span>
          {slot === value && <Check size={20} aria-hidden="true" />}
        </button>)}
      </div>
    </div>}
  </div>;
}
